import axios from "axios";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  (typeof window !== "undefined" ? window.location.origin : "");
export const API_BASE = `${BACKEND_URL}/api`;
export const TOKEN_KEY = "aoa_admin_token";

export const adminApi = axios.create({
  baseURL: `${API_BASE}/admin`,
  headers: { "Content-Type": "application/json" },
  timeout: 30000,
});

adminApi.interceptors.request.use((cfg) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

adminApi.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      const path = window.location.pathname;
      if (!path.startsWith("/admin/login")) {
        localStorage.removeItem(TOKEN_KEY);
        window.location.replace("/admin/login");
      }
    }
    return Promise.reject(err);
  },
);

// Convenience: a separate client for multipart uploads
export const adminUpload = axios.create({
  baseURL: `${API_BASE}/admin`,
  timeout: 60000,
});
adminUpload.interceptors.request.use((cfg) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export function formatError(err) {
  const d = err?.response?.data?.detail ?? err?.response?.data ?? err?.message;
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" · ");
  if (d?.msg) return d.msg;
  return String(d ?? "Something went wrong");
}

export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);
export const getToken = () => localStorage.getItem(TOKEN_KEY);

// Endpoint helpers
export const adminAuth = {
  login: (data) => adminApi.post("/auth/login", data).then((r) => r.data),
  changePassword: (data) => adminApi.post("/auth/change-password", data).then((r) => r.data),
  logout: () => adminApi.post("/auth/logout").then((r) => r.data),
  me: () => adminApi.get("/me").then((r) => r.data),
  patchMe: (data) => adminApi.patch("/me", data).then((r) => r.data),
};

export const adminPages = {
  list: () => adminApi.get("/pages").then((r) => r.data),
  get: (slug) => adminApi.get(`/pages/${slug}`).then((r) => r.data),
  patchSection: (slug, key, data) =>
    adminApi.patch(`/pages/${slug}/sections/${key}`, data).then((r) => r.data),
  patchSeo: (slug, data) => adminApi.patch(`/pages/${slug}/seo`, data).then((r) => r.data),
  put: (slug, data) => adminApi.put(`/pages/${slug}`, data).then((r) => r.data),
};

export const adminSettings = {
  get: () => adminApi.get("/site-settings").then((r) => r.data),
  put: (data) => adminApi.put("/site-settings", data).then((r) => r.data),
  putLogos: (data) => adminApi.put("/site-settings/logos", data).then((r) => r.data),
};

export const adminNews = {
  list: () => adminApi.get("/news").then((r) => r.data),
  get: (id) => adminApi.get(`/news/${id}`).then((r) => r.data),
  create: (data) => adminApi.post("/news", data).then((r) => r.data),
  update: (id, data) => adminApi.put(`/news/${id}`, data).then((r) => r.data),
  remove: (id) => adminApi.delete(`/news/${id}`).then((r) => r.data),
};

export const adminResources = {
  list: () => adminApi.get("/resources").then((r) => r.data),
  get: (id) => adminApi.get(`/resources/${id}`).then((r) => r.data),
  create: (data) => adminApi.post("/resources", data).then((r) => r.data),
  update: (id, data) => adminApi.put(`/resources/${id}`, data).then((r) => r.data),
  remove: (id) => adminApi.delete(`/resources/${id}`).then((r) => r.data),
};

export const adminPartners = {
  list: () => adminApi.get("/partners").then((r) => r.data),
  get: (id) => adminApi.get(`/partners/${id}`).then((r) => r.data),
  create: (data) => adminApi.post("/partners", data).then((r) => r.data),
  update: (id, data) => adminApi.put(`/partners/${id}`, data).then((r) => r.data),
  remove: (id) => adminApi.delete(`/partners/${id}`).then((r) => r.data),
};

export const adminMessages = {
  list: (params = {}) => adminApi.get("/contact-submissions", { params }).then((r) => r.data),
  get: (id) => adminApi.get(`/contact-submissions/${id}`).then((r) => r.data),
  patch: (id, data) => adminApi.patch(`/contact-submissions/${id}`, data).then((r) => r.data),
  remove: (id) => adminApi.delete(`/contact-submissions/${id}`).then((r) => r.data),
};

export const adminUploads = {
  upload: (file, purpose = "image") => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("purpose", purpose);
    return adminUpload.post("/uploads", fd).then((r) => r.data);
  },
};
